import React, { useState } from 'react';
import { Shield, CheckCircle, AlertTriangle, TrendingUp, Target, Clock, Scale, Gavel, FileText, Users, ChevronDown, DollarSign } from 'lucide-react';
import CTASection from '../../components/CTASection';
import RegistrationModal from '../../components/RegistrationModal';

const PatentEnforcementPage = () => {
  const [openFAQ, setOpenFAQ] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState('registration');

  const openModal = (type) => {
    setModalType(type);
    setIsModalOpen(true);
  };

  const benefits = [
    {
      icon: Shield,
      title: 'Protect Your Rights',
      description: 'Stop infringers from profiting from your patented technology'
    },
    {
      icon: DollarSign,
      title: 'Monetary Recovery',
      description: 'Recover damages, lost profits, and royalties from infringement'
    },
    {
      icon: Target,
      title: 'Market Exclusivity',
      description: 'Maintain your competitive advantage by blocking unauthorized use'
    },
    {
      icon: Gavel,
      title: 'Legal Expertise',
      description: 'Experienced patent litigators with proven track records'
    }
  ];

  const enforcementOptions = [
    {
      type: 'Cease & Desist Letter',
      description: 'Formal notice demanding infringer stop using your patented invention',
      when: 'First step to put infringer on notice and attempt resolution',
      cost: '$2,500 - $5,000',
      success: '40% resolve at this stage'
    },
    {
      type: 'Licensing Negotiation',
      description: 'Convert infringement into licensing revenue through negotiation',
      when: 'When ongoing relationship or royalty income is preferable to litigation',
      cost: '$5,000 - $15,000',
      success: '30% result in license agreements'
    },
    {
      type: 'ITC Investigation',
      description: 'Import ban through International Trade Commission for foreign manufacturers',
      when: 'Infringing products are manufactured abroad and imported to US',
      cost: '$500K - $2M',
      success: 'Faster than federal court, 12-18 months'
    },
    {
      type: 'Patent Litigation',
      description: 'Federal court lawsuit seeking injunction and monetary damages',
      when: 'When cease & desist fails or significant damages warrant litigation',
      cost: '$1M - $5M+',
      success: 'Average settlement: $5M - $50M'
    }
  ];

  const enforcementServices = [
    {
      name: 'Infringement Analysis',
      price: '$5,000',
      description: 'Expert analysis to determine if your patent is being infringed',
      features: ['Claim-by-claim comparison', 'Infringement opinion letter', 'Validity assessment', 'Evidence collection strategy', 'Risk/reward analysis', '2-week turnaround']
    },
    {
      name: 'Cease & Desist + Negotiation',
      price: '$10,000',
      description: 'Formal demand letter and licensing negotiation services',
      features: ['All infringement analysis', 'Cease & desist letter drafting', 'Negotiation strategy', 'Licensing term sheets', 'Settlement agreement drafting', 'Attorney representation'],
      recommended: true
    },
    {
      name: 'Patent Litigation',
      price: 'Custom Quote',
      description: 'Full litigation services from filing to trial or settlement',
      features: ['Complaint drafting and filing', 'Discovery and depositions', 'Expert witness coordination', 'Motion practice', 'Trial preparation', 'Appeals if necessary']
    }
  ];

  const caseStudies = [
    {
      title: 'Software Patent Enforcement: $12M Settlement',
      scenario: 'SaaS company discovered competitor using patented data processing algorithm.',
      problem: 'Competitor had raised $50M and was gaining significant market share.',
      solution: 'Filed federal lawsuit with strong infringement evidence and validity opinion. Negotiated settlement during discovery phase.',
      outcome: '$12M settlement plus ongoing 5% royalty on competitor\'s revenue. Total value: $25M+ over 5 years.'
    },
    {
      title: 'Medical Device: ITC Import Ban Achieved',
      scenario: 'US manufacturer faced competition from Chinese knockoff medical devices.',
      problem: 'Foreign manufacturer selling identical devices at 60% lower price.',
      solution: 'Filed ITC investigation showing clear infringement and US industry harm. Obtained exclusion order banning imports.',
      outcome: 'Import ban issued in 14 months. Competitor forced to exit US market. Client regained 85% market share.'
    }
  ];

  const timeline = [
    { time: 'Week 1-2', activity: 'Infringement analysis: claim mapping, evidence gathering, validity check' },
    { time: 'Week 3-4', activity: 'Strategy session: evaluate enforcement options, costs, potential outcomes' },
    { time: 'Month 2', activity: 'Cease & desist letter sent, infringer typically responds within 30 days' },
    { time: 'Month 2-4', activity: 'Negotiation phase: licensing discussions or settlement talks' },
    { time: 'Month 4-6', activity: 'If unresolved: file complaint in federal court or initiate ITC investigation' },
    { time: 'Month 6-18', activity: 'Discovery: document production, depositions, expert reports' },
    { time: 'Month 18-24', activity: 'Trial or settlement: 95% settle before trial, average $5M-$50M' }
  ];

  const faqs = [
    {
      question: 'How do I know if someone is infringing my patent?',
      answer: 'Patent infringement occurs when someone makes, uses, sells, or imports your patented invention without permission. To determine infringement, we perform claim-by-claim analysis comparing the allegedly infringing product/process to each element of your patent claims. Infringement requires that the accused product meets every element of at least one patent claim (literal infringement) or is equivalent under the doctrine of equivalents. We analyze product specifications, reverse engineering reports, marketing materials, and technical documentation to build an infringement case.'
    },
    {
      question: 'What damages can I recover from patent infringement?',
      answer: 'US patent law provides several types of damages: Lost profits (profits you would have made but for the infringement), reasonable royalty (minimum compensation based on hypothetical licensing negotiation), treble damages (up to 3x actual damages for willful infringement after notice), and attorney fees in exceptional cases. Average patent damages awards range from $5M to $50M depending on patent strength, market size, and infringement duration. Injunctions can also force competitors to stop selling infringing products entirely.'
    },
    {
      question: 'Should I send a cease & desist letter or file a lawsuit immediately?',
      answer: 'We almost always recommend starting with a cease & desist letter for several reasons: 40% of cases settle after demand letter, avoiding litigation costs; establishes willfulness for potential treble damages if litigation becomes necessary; opens negotiation for licensing revenue instead of costly litigation; and gives you information about infringer\'s defenses and willingness to fight. However, immediate filing may be warranted if: the infringer is a large, well-funded company unlikely to respond, you need immediate injunctive relief, or the statute of limitations is approaching.'
    },
    {
      question: 'How long does patent litigation take and what does it cost?',
      answer: 'Patent litigation typically takes 2-4 years from filing to trial, though 95% settle before trial. Costs vary significantly: Small cases (<$1M at risk): $500K-$1M in legal fees. Medium cases ($1M-$25M at risk): $1M-$3M in legal fees. Large cases (>$25M at risk): $3M-$10M+ in legal fees. Many patent attorneys work on contingency (20-40% of recovery) for strong cases, eliminating upfront costs. Average settlement is $5M-$50M, making litigation economically viable for valuable patents with clear infringement.'
    },
    {
      question: 'What is an ITC investigation and when should I use it?',
      answer: 'The International Trade Commission (ITC) is an alternative to federal court for stopping patent infringement by imported products. Benefits: Faster timeline (12-18 months vs 3-5 years), exclusion orders ban imports at the border, and no monetary damages reduces defendant\'s incentive to fight. ITC is best when: the infringing products are manufactured abroad, you want to stop imports quickly, the infringer has limited US assets, or you want to pressure settlement. Requirements: show a US industry exists (manufacturing, R&D, or licensing activities in US).'
    },
    {
      question: 'Can the infringer challenge my patent validity during enforcement?',
      answer: 'Yes, validity challenges are the primary defense in patent litigation. Infringers will argue: anticipation (§102) - prior art shows the invention; obviousness (§103) - invention would be obvious to skilled person; or indefiniteness (§112) - claims are not clear. They may also file Inter Partes Review (IPR) at the Patent Office, which has a 60-70% rate of invalidating some claims. This is why we conduct thorough validity analysis before enforcement to assess litigation risk and identify potential weaknesses in advance.'
    }
  ];

  const infringementTypes = [
    {
      type: 'Direct Infringement',
      description: 'Making, using, selling, or importing the patented invention',
      example: 'Competitor manufactures device identical to your patented product'
    },
    {
      type: 'Indirect Infringement',
      description: 'Inducing others to infringe or selling components for infringing use',
      example: 'Selling kit that customers assemble into infringing device'
    },
    {
      type: 'Willful Infringement',
      description: 'Infringement with knowledge of patent or after receiving notice',
      example: 'Continuing infringement after receiving cease & desist letter (enables treble damages)'
    }
  ];

  const strategicConsiderations = [
    { consideration: 'Patent strength', impact: 'Strong patents with clear claims and solid prosecution history have higher success rates' },
    { consideration: 'Infringer size', impact: 'Large companies have resources to fight but also more to lose; small companies may lack assets for meaningful recovery' },
    { consideration: 'Market impact', impact: 'Calculate lost sales, market share erosion, and price depression caused by infringement' },
    { consideration: 'Validity risks', impact: 'Assess likelihood of successful invalidity challenge before enforcement' },
    { consideration: 'Alternative strategies', impact: 'Consider licensing, acquisition, or strategic partnership over pure litigation' }
  ];

  return (
    <div>
      {/* Hero */}
      <div className="bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-block bg-[#168356]/20 text-[#168356] px-4 py-2 rounded-full text-sm font-semibold mb-6">
              Patent Enforcement & Litigation
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Enforce Your Patents, <span className="text-[#168356]">Protect Your Market</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Stop patent infringers and recover damages with strategic enforcement. Our experienced patent litigators help you defend your innovations and maximize the value of your patent portfolio.
            </p>
            <div className="flex gap-4">
              <button onClick={() => openModal('registration')} className="bg-[#168356] hover:bg-[#0a5938] text-white font-bold px-8 py-4 rounded-lg transition-all">
                Free Infringement Analysis
              </button>
              <button onClick={() => openModal('registration')} className="border-2 border-white text-white hover:bg-white hover:text-black font-bold px-8 py-4 rounded-lg transition-all">
                Schedule Consultation
              </button>
            </div>
            <div className="flex items-center gap-6 mt-8 text-sm">
              <span className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-[#168356]" />
                Patent Litigation Experience
              </span>
              <span className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-[#168356]" />
                $5M-$50M Avg Settlement
              </span>
              <span className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#168356]" />
                Contingency Available
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Section */}
      <div className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-[#168356] mb-2">95%</div>
              <div className="text-gray-600 text-sm">Cases settle before trial</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-[#168356] mb-2">$5M-$50M</div>
              <div className="text-gray-600 text-sm">Average settlement range</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-[#168356] mb-2">3x</div>
              <div className="text-gray-600 text-sm">Potential damages for willfulness</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-[#168356] mb-2">40%</div>
              <div className="text-gray-600 text-sm">Resolve after cease & desist</div>
            </div>
          </div>
        </div>
      </div>

      {/* What Section */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block bg-[#168356]/10 text-[#168356] px-4 py-2 rounded-full text-sm font-semibold mb-6">
                What is Patent Enforcement?
              </div>
              <h2 className="text-4xl font-bold text-black mb-6">Stop Infringers and Protect Your Investment</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Patent enforcement is the process of identifying, stopping, and recovering damages from parties who are using your patented technology without permission. Your patent grants you exclusive rights, but enforcement is necessary to stop infringement and maintain your competitive advantage.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Enforcement options range from sending cease & desist letters to full federal court litigation. The strategy depends on factors like the strength of your patent, the size of the infringer, potential damages, and your business objectives. Many cases resolve through licensing negotiations, avoiding costly litigation while generating revenue.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Patent litigation can be expensive ($1M-$5M+), but damages awards and settlements typically range from $5M to $50M or more. For strong patents with clear infringement, many firms (including ours) offer contingency arrangements, eliminating upfront costs.
              </p>
              <p className="text-lg font-semibold text-black">
                Don't let infringers profit from your innovation. Strategic enforcement protects your market position and can generate significant licensing revenue or damages awards.
              </p>
            </div>
            <div className="space-y-4">
              {infringementTypes.map((type, index) => (
                <div key={index} className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 border border-gray-200">
                  <h3 className="text-xl font-bold text-black mb-2">{type.type}</h3>
                  <p className="text-gray-700 mb-3 text-sm">{type.description}</p>
                  <div className="bg-white rounded-lg p-3 border-l-4 border-[#168356]">
                    <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Example</div>
                    <div className="text-sm text-gray-700">{type.example}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Enforcement Options */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Patent Enforcement Options</h2>
            <p className="text-lg text-gray-600">Choose the right strategy based on your goals and circumstances</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {enforcementOptions.map((option, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg border border-gray-200">
                <h3 className="text-2xl font-bold text-black mb-3">{option.type}</h3>
                <p className="text-gray-700 mb-4">{option.description}</p>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="font-semibold text-gray-900">When to use:</span>
                    <p className="text-gray-600 mt-1">{option.when}</p>
                  </div>
                  <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                    <div>
                      <span className="text-xs text-gray-500 uppercase">Typical Cost</span>
                      <div className="text-[#168356] font-bold">{option.cost}</div>
                    </div>
                    <div className="text-right">
                      <span className="text-xs text-gray-500 uppercase">Success Rate</span>
                      <div className="text-green-600 font-bold text-sm">{option.success}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Services & Pricing */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Enforcement Services & Pricing</h2>
            <p className="text-lg text-gray-600">From initial analysis to full litigation representation</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {enforcementServices.map((service, index) => (
              <div key={index} className={`bg-white rounded-xl p-8 shadow-lg border-2 ${service.recommended ? 'border-[#168356] transform scale-105' : 'border-gray-200'}`}>
                {service.recommended && (
                  <div className="bg-[#168356] text-white text-sm font-bold px-4 py-1 rounded-full inline-block mb-4">
                    Most Popular
                  </div>
                )}
                <h3 className="text-2xl font-bold text-black mb-2">{service.name}</h3>
                <div className="text-4xl font-bold text-[#168356] mb-4">{service.price}</div>
                <p className="text-gray-600 mb-6">{service.description}</p>
                <ul className="space-y-3 mb-6">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                      <CheckCircle className="w-4 h-4 text-[#168356] flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <button onClick={() => openModal('registration')} className={`w-full py-3 rounded-lg font-semibold transition-all ${service.recommended ? 'bg-[#168356] text-white hover:bg-[#0a5938]' : 'bg-gray-100 text-black hover:bg-gray-200'}`}>
                  Get Started
                </button>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <p className="text-gray-600 text-sm">
              <strong>Contingency Fee Available:</strong> For strong cases, we offer 20-40% contingency arrangements with no upfront legal fees.
            </p>
          </div>
        </div>
      </div>

      {/* Strategic Considerations */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Strategic Enforcement Considerations</h2>
            <p className="text-lg text-gray-600">Factors we evaluate before enforcement action</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {strategicConsiderations.map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-6 border border-gray-200">
                <Target className="w-10 h-10 text-[#168356] mb-4" />
                <h3 className="text-lg font-bold text-black mb-2 capitalize">{item.consideration}</h3>
                <p className="text-gray-600 text-sm">{item.impact}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Case Studies */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Enforcement Success Stories</h2>
            <p className="text-lg text-gray-600">See how we protected client patents and recovered significant damages</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {caseStudies.map((study, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-black mb-4">{study.title}</h3>
                <div className="space-y-4">
                  <div>
                    <div className="text-sm font-semibold text-gray-500 uppercase mb-2">Scenario</div>
                    <p className="text-gray-700">{study.scenario}</p>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-red-500 uppercase mb-2">Problem</div>
                    <p className="text-gray-700">{study.problem}</p>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-[#168356] uppercase mb-2">Our Solution</div>
                    <p className="text-gray-700">{study.solution}</p>
                  </div>
                  <div className="pt-4 border-t border-gray-200">
                    <div className="text-sm font-semibold text-green-600 uppercase mb-2">Outcome</div>
                    <p className="text-gray-700 font-semibold">{study.outcome}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Enforcement Timeline</h2>
            <p className="text-lg text-gray-600">From analysis to settlement or trial</p>
          </div>
          <div className="max-w-4xl mx-auto">
            {timeline.map((item, index) => (
              <div key={index} className="flex gap-6 mb-8 last:mb-0">
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 bg-[#168356] rounded-full flex items-center justify-center text-white font-bold">
                    {index + 1}
                  </div>
                  {index < timeline.length - 1 && (
                    <div className="w-0.5 h-16 bg-[#168356]"></div>
                  )}
                </div>
                <div className="flex-1 pb-8">
                  <div className="text-[#168356] font-semibold mb-2">{item.time}</div>
                  <div className="text-gray-700 text-lg">{item.activity}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Benefits of Patent Enforcement</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
                <div className="w-14 h-14 bg-[#168356] rounded-full flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-black mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* FAQs */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl overflow-hidden shadow-sm">
                <button
                  onClick={() => setOpenFAQ(openFAQ === index ? -1 : index)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <span className="text-left font-semibold text-black pr-8">{faq.question}</span>
                  <ChevronDown className={`w-5 h-5 text-[#168356] flex-shrink-0 transition-transform ${openFAQ === index ? 'rotate-180' : ''}`} />
                </button>
                {openFAQ === index && (
                  <div className="px-6 pb-4 text-gray-700 leading-relaxed">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <CTASection />

      <RegistrationModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        initialType={modalType}
      />
    </div>
  );
};

export default PatentEnforcementPage;